import { Link } from "react-router-dom";
import { teamMembers, awards } from "@/mocks/pagesData";
import { stats } from "@/mocks/homeData";
import CareersForm from "./components/CareersForm";

const missionVision = [
  { icon: "ri-focus-3-line", title: "Our Mission", desc: "To empower organizations with agentic, ethical, and high-impact AI, unlocking new levels of efficiency, intelligence, and growth." },
  { icon: "ri-eye-line", title: "Our Vision", desc: "To become the most trusted partner for AI-led business transformation across the Americas and Africa." },
];

const timeline = [
  { year: "1996", event: "Founded in Lagos, Nigeria as a technology consulting firm" },
  { year: "2005", event: "Expanded services to cloud infrastructure and enterprise IT" },
  { year: "2012", event: "Opened North American headquarters in Chicago, USA" },
  { year: "2018", event: "Launched CPI Academy: global IT training & certification" },
  { year: "2021", event: "Pivoted to AI-first consulting: agentic systems & LLMs" },
  { year: "2024", event: "Named Top AI Consultancy by Clutch.co; 500+ AI projects delivered" },
];

const globalPartners = [
  { name: "Microsoft", desc: "Azure AI & Copilot Partner" },
  { name: "AWS", desc: "Advanced Consulting Partner" },
  { name: "Google Cloud", desc: "Vertex AI & Workspace Partner" },
  { name: "i3Cert", desc: "Global Certification Partner" },
];

export default function AboutPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Chicago%20city%20skyline%20aerial%20view%20dusk%20golden%20hour%20dramatic%20sky%20professional%20cinematic%20wide%20landscape%20with%20Lagos%20Nigeria%20Africa%20connection%20global%20technology&width=1440&height=600&seq=aiabouthero&orientation=landscape"
            alt="CPI Technologies global offices"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/97 to-black/60" />
        </div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C8F135]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#C8F135]/10 border border-[#C8F135]/20 rounded-full px-4 py-1.5 mb-6">
              <span className="text-[#C8F135] text-xs font-semibold uppercase tracking-wider">AI-First · Global · Trusted</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-5">
              About <span style={{color: "#C8F135"}}>CPI Technologies</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mb-6">
              Founded in 1996 in Lagos, Nigeria. Headquartered today in Chicago, USA. Delivering AI-led transformation across the Americas and Africa.
            </p>
            <p className="text-slate-400 text-base leading-relaxed italic border-l-2 pl-4" style={{borderColor: "#C8F135"}}>
              &ldquo;Together, We Make AI Happen.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* Story + Mission/Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-1.5 mb-6">
                <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Our Story</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">
                Our Legacy is Technology.
                <span style={{color: "#9bc400"}}> Our Future is AI.</span>
              </h2>
              <p className="text-slate-600 text-base leading-relaxed mb-5">CPI Technologies Inc was founded in 1996 in Lagos, Nigeria, and today operates as a global AI and technology consulting firm headquartered in Chicago, USA, with a strong presence across Africa and North America.</p>
              <p className="text-slate-600 text-base leading-relaxed mb-5">We partner with Fortune 500 companies, government institutions, and high-growth businesses to adopt AI responsibly, build intelligent systems, and create measurable business advantage.</p>
              <p className="text-slate-600 text-base leading-relaxed mb-8">We combine deep enterprise IT experience in cloud, cybersecurity, and software with specialized AI expertise to deliver solutions that are ethical, scalable, and business-aligned.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {missionVision.map((item) => (
                  <div key={item.title} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#C8F135]/20 mb-3">
                      <i className={`${item.icon} text-[#9bc400]`} />
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm mb-2">{item.title}</h3>
                    <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="rounded-xl overflow-hidden w-full h-80 mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=diverse%20professional%20AI%20technology%20consulting%20team%20Chicago%20office%20collaboration%20modern%20workspace%20bright%20natural%20light%20authentic%20candid%20warm%20tones%20wide&width=720&height=480&seq=aiabout2&orientation=landscape"
                  alt="CPI Technologies AI team"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white rounded-xl p-5 grid grid-cols-2 gap-4 border border-slate-100">
                {stats.map((s) => (
                  <div key={s.label} className="text-center py-2">
                    <div className="text-2xl font-black" style={{color: "#9bc400"}}>{s.value}</div>
                    <div className="text-xs text-slate-400 mt-0.5 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-1.5 mb-5">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">Our Journey</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              28 Years of <span style={{color: "#9bc400"}}>Innovation</span>
            </h2>
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-200" />
            <div className="flex flex-col gap-8">
              {timeline.map((item, i) => (
                <div key={item.year} className="flex items-start gap-6 pl-0">
                  <div className="relative flex-shrink-0">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-full border-2 text-xs font-black z-10 relative ${
                      i === timeline.length - 1
                        ? "bg-[#C8F135] border-[#C8F135] text-black"
                        : "bg-white border-slate-200 text-slate-700"
                    }`}>
                      {item.year.slice(2)}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-100 flex-1">
                    <div className="font-bold text-sm mb-1" style={{color: "#9bc400"}}>{item.year}</div>
                    <p className="text-slate-700 text-sm leading-relaxed">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Global Partners */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-slate-900 mb-2">Global Partners</h2>
            <p className="text-slate-500 text-sm">Trusted by the world&apos;s leading technology platforms</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {globalPartners.map((p) => (
              <div key={p.name} className="flex items-center gap-3 bg-slate-50 rounded-xl px-6 py-4 border border-slate-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C8F135]/20 flex-shrink-0">
                  <i className="ri-award-line text-[#9bc400]" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-400">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {awards.map((a) => (
              <div key={a.title} className="flex items-center gap-3 bg-white rounded-xl px-5 py-3 border border-slate-100">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#C8F135]/20 flex-shrink-0">
                  <i className="ri-trophy-line text-[#9bc400]" />
                </div>
                <div>
                  <div className="text-xs font-bold text-slate-900">{a.title}</div>
                  <div className="text-xs text-slate-400">{a.org} · {a.year}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-1.5 mb-5">
              <span className="text-slate-500 text-xs font-semibold uppercase tracking-wider">The Team</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              Meet the <span style={{color: "#9bc400"}}>AI Experts</span>
            </h2>
            <p className="text-slate-500 text-base mt-3 max-w-xl mx-auto">120+ AI engineers, consultants, and trainers united by a mission to make AI work for your business.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {teamMembers.map((member) => (
              <div key={member.id} className="bg-slate-50 rounded-xl p-6 border border-slate-100 hover:-translate-y-1 transition-transform duration-300 cursor-default">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center text-lg font-black text-white bg-slate-800 mb-4">{member.avatar}</div>
                <h3 className="font-bold text-slate-900 text-base mb-0.5">{member.name}</h3>
                <p className="text-xs font-semibold mb-3" style={{color: "#9bc400"}}>{member.role}</p>
                <p className="text-slate-500 text-xs leading-relaxed mb-4">{member.bio}</p>
                <a href={member.linkedin} rel="nofollow" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill" /> LinkedIn
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CareersForm />
    </main>
  );
}
